import { applyLoadNotesToBuckets, loadNotesReceived } from './loadNotes'
import {
    BolCheck,
    ClientOngoingLoad,
    ClientOngoingTrip,
    DeadheadOrigin,
    LoadFinanceStatus,
    MergedItem,
    RecommendationSummary,
    StopTypeFilter,
    TripStop,
    Truck,
} from '@numeo/types-core'
import { Pagination, type TripDateBasis } from '@numeo/types-main-web-app'
import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit'
import type { OngoingLoadsFinancialSummary } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'
import { AssignResult } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/components/types'
import type { TabType, OngoingLoadsTableUiVersion } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/types'
import { type OngoingLoadTabCounts, tabToCountKey } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/utils/tab-counts'
import { RootState } from 'store'
import { readLocalStorageValue, writeLocalStorageValue } from '@/utils/safeLocalStorage'

const ROWS_PER_PAGE_STORAGE_KEY = 'ongoing-loads-rows-per-page'
const DEFAULT_ROWS_PER_PAGE = 10

/**
 * Which Ongoing Loads table/toolbar the user is looking at. The redesigned table ships alongside
 * the original one rather than replacing it, so the choice has to live somewhere both the toolbar
 * (which renders the switch) and the table (which reads it) can see — hence the store rather than
 * local state. The rollout uses a new storage key so every browser starts on v2, then persists
 * any layout choice made after the rollout, including switching back to the classic table.
 */
const TABLE_UI_VERSION_STORAGE_KEY = 'ongoing-loads-table-ui-version-v2'
const DEFAULT_TABLE_UI_VERSION: OngoingLoadsTableUiVersion = 'v2'

const readPersistedTableUiVersion = (): OngoingLoadsTableUiVersion =>
    readLocalStorageValue(TABLE_UI_VERSION_STORAGE_KEY, DEFAULT_TABLE_UI_VERSION, (stored) =>
        stored === 'v2' || stored === 'v1' ? stored : DEFAULT_TABLE_UI_VERSION
    )

const persistTableUiVersion = (version: OngoingLoadsTableUiVersion): void => {
    writeLocalStorageValue(TABLE_UI_VERSION_STORAGE_KEY, version)
}

const readPersistedRowsPerPage = (): number =>
    readLocalStorageValue(ROWS_PER_PAGE_STORAGE_KEY, DEFAULT_ROWS_PER_PAGE, (storedValue) => {
        const stored = Number(storedValue)
        return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_ROWS_PER_PAGE
    })

const persistRowsPerPage = (rowsPerPage: number): void => {
    writeLocalStorageValue(ROWS_PER_PAGE_STORAGE_KEY, String(rowsPerPage))
}

type WizardStep = 1 | 2 | 3
// Mirrors DeadheadOriginPicker's own CardKey — kept as a standalone type here (not imported from the
// component) since the store shouldn't depend on a specific feature's UI component.
type DeadheadOriginSource = 'previous' | 'gps' | 'address'
/** A payroll batch picked in the Batch filter: the id goes to the server, the label stays for the chip, so it reads right after a search moves on. */
export interface PayrollBatchFilterOption {
    id: string
    label: string
}

export type LoadProcessingStatus = {
    status: 'pending' | 'processing' | 'success' | 'error' | 'warning'
    message?: string
}

/** The All Trips date range: two `MM/dd/yyyy` days and which day of the trip they match. */
export type AllTripsDateRange = LoadsState['allTripsDateRange']

interface LoadsState {
    selectedLoadsIds: string[]
    activeTab: TabType
    loading: boolean
    isUpdating: boolean
    error: string | null
    counts: OngoingLoadTabCounts
    financialSummaries: {
        upcoming: OngoingLoadsFinancialSummary
        dispatched: OngoingLoadsFinancialSummary
        inTransit: OngoingLoadsFinancialSummary
        history: OngoingLoadsFinancialSummary
        allTrips: OngoingLoadsFinancialSummary
    }
    isBolCheckSidebarOpen: boolean
    selectedBolCheck: BolCheck | null
    selectedLoad: ClientOngoingLoad | null
    deleteConfirmModalOpen: boolean
    ongoingLoadCounts: OngoingLoadTabCounts
    page: number
    rowsPerPage: number
    fileUploadModalOpen: boolean
    rateConsModalOpen: boolean
    shouldFetchLoads: boolean
    inTransitLoads: ClientOngoingLoad[]
    dispatchedLoads: ClientOngoingLoad[]
    historyLoads: ClientOngoingLoad[]
    allTripsLoads: ClientOngoingLoad[]
    upcomingLoads: ClientOngoingLoad[]
    tabToReset: TabType | null
    upcomingPagination: Pagination
    dispatchedPagination: Pagination
    inTransitPagination: Pagination
    historyPagination: Pagination
    allTripsPagination: Pagination
    selectedLoadId: string | null
    selectedTruck: Truck | undefined
    loadProcessingStatus: Record<string, LoadProcessingStatus>
    multiAssignResult: AssignResult | null
    isLoadAssigning: boolean
    multiAssignStep: WizardStep
    currentTrip: ClientOngoingTrip | null
    selectedLoads: ClientOngoingLoad[]
    isTripActionLoading: 'cancel' | 'archive' | 'delete' | null
    isTripDeleteDialogOpen: boolean
    isTripArchiveDialogOpen: boolean
    isTripCancelDialogOpen: boolean
    loadAssignmentMode: 'single' | 'multi'
    mergedItems: MergedItem[]
    trips: ClientOngoingTrip[]
    tripsById: Record<string, ClientOngoingTrip>
    tabTripIds: Record<TabType, string[]>
    loadAssigmentMessageEditorOpen: boolean
    // The assignment modal was opened from the status menu to dispatch a load that already has its truck:
    // it starts on the message step, has no truck step to go back to, and keeps the load's deadhead.
    isDispatchingAssignedLoad: boolean
    assignTruckMenuOpen: boolean
    tourAssigmentPreview: boolean
    tourTruckAssigmentMessage: string
    tripAssigmentMessageEditorOpen: boolean
    multiAssignPlannedStops: TripStop[]
    // Filtering
    sort: string // '' | 'delay-asc' | 'delay-desc' | 'delivery-status' | 'pickup-asc' | 'pickup-desc' | 'delivery-asc' | 'delivery-desc'
    dispatcherFilter: string[] // 'all' | 'unassigned' | 'me' | dispatcherId
    driverFilter: string[] // 'all' | driverId
    dispatcherAssignmentFilter: 'all' | 'assigned' | 'unassigned'
    selectedLoadGroupId: string | null
    searchQuery: string
    brokerFilter: string[]
    brokerFilterMode: 'filter' | 'priority' // 'filter' = only show selected, 'priority' = show all but prioritize selected
    laneFilterMode: 'all' | 'only' | 'exclude' // 'only' = only lane loads, 'exclude' = hide lane loads
    tagFilter: string[] // tag ids (or 'none' for untagged loads); a load matches when it carries any of them. [] = no filter
    payrollBatchFilter: PayrollBatchFilterOption[] // payroll batches a load was paid in, with their labels for the chips. [] = no filter
    includePayrollPlacement: boolean // the Batch/Period column is on screen, so the list asks the server for it (an extra read otherwise skipped)
    stopTypeFilter: StopTypeFilter[] // filters by the load's current stop (deliveryStatus.stopIndex into route); ['all'] = no filter
    editLoadModalOpen: boolean
    editLoadInitialStopIndex: number | null
    expandedLoads: string[]
    financeStatusFilter: LoadFinanceStatus | 'all'
    attentionNeededFilter: boolean
    unassignedRiskFilter: number[]
    allTripsDateRange: {
        startDate: string
        endDate: string
        /** Which day of the trip the range matches; the pickup day when left out (a cleared range drops it). */
        basis?: TripDateBasis
    }
    loadsWithRecommendations: ClientOngoingLoad[]
    // Counts for the Smart Suggestions banner shown on page open. The loads themselves are fetched on
    // demand into loadsWithRecommendations only when the user expands the banner.
    recommendationSummary: RecommendationSummary | null
    // The deadhead origin chosen in "Choose a Truck" (TruckAssignmentModal), carried into that modal's
    // dispatch message step — the step has no picker of its own, it just uses whatever was already
    // decided. `null` = nothing deliberately chosen (default pipeline).
    selectedDeadheadOrigin: DeadheadOrigin | null
    // Which picker card produced `selectedDeadheadOrigin` — "previous"/"gps" are always re-derivable
    // from the truck/load's current state, so callers submitting this to the backend must send `null`
    // (not the resolved value) for those, letting the backend keep deriving it live as schedules shift;
    // only "address" is a genuinely manual pick that must be frozen as-is.
    selectedDeadheadOriginSource: DeadheadOriginSource | null
    tableUiVersion: OngoingLoadsTableUiVersion
}

const persistedRowsPerPage = readPersistedRowsPerPage()

const initialState: LoadsState = {
    selectedLoadsIds: [],
    activeTab: 'in-transit',
    tableUiVersion: readPersistedTableUiVersion(),
    loading: false,
    isUpdating: false,
    error: null,
    counts: { upcoming: 0, dispatched: 0, inTransit: 0, history: 0, allTrips: 0 },
    financialSummaries: {
        upcoming: { brokerRate: 0, assigneeRate: 0, reserveBalance: 0, completedTrips: 0 },
        dispatched: { brokerRate: 0, assigneeRate: 0, reserveBalance: 0, completedTrips: 0 },
        inTransit: { brokerRate: 0, assigneeRate: 0, reserveBalance: 0, completedTrips: 0 },
        history: { brokerRate: 0, assigneeRate: 0, reserveBalance: 0, completedTrips: 0 },
        allTrips: { brokerRate: 0, assigneeRate: 0, reserveBalance: 0, completedTrips: 0 },
    },
    isBolCheckSidebarOpen: false,
    selectedBolCheck: null,
    selectedLoad: null,
    deleteConfirmModalOpen: false,
    ongoingLoadCounts: { upcoming: 0, dispatched: 0, inTransit: 0, history: 0, allTrips: 0 },
    page: 1,
    rowsPerPage: persistedRowsPerPage,
    fileUploadModalOpen: false,
    rateConsModalOpen: false,
    shouldFetchLoads: false,
    inTransitLoads: [],
    dispatchedLoads: [],
    historyLoads: [],
    allTripsLoads: [],
    upcomingLoads: [],
    tabToReset: null,
    upcomingPagination: {
        page: 1,
        limit: persistedRowsPerPage,
        pages: 0,
        total: 0,
    },
    dispatchedPagination: {
        page: 1,
        limit: persistedRowsPerPage,
        pages: 0,
        total: 0,
    },
    inTransitPagination: {
        page: 1,
        limit: persistedRowsPerPage,
        pages: 0,
        total: 0,
    },
    historyPagination: {
        page: 1,
        limit: persistedRowsPerPage,
        pages: 0,
        total: 0,
    },
    allTripsPagination: {
        page: 1,
        limit: persistedRowsPerPage,
        pages: 0,
        total: 0,
    },
    selectedLoadId: null,
    selectedTruck: undefined,
    loadProcessingStatus: {},
    multiAssignResult: null,
    isLoadAssigning: false,
    multiAssignStep: 1,
    currentTrip: null,
    selectedLoads: [],
    isTripActionLoading: null,
    isTripDeleteDialogOpen: false,
    isTripArchiveDialogOpen: false,
    isTripCancelDialogOpen: false,
    loadAssignmentMode: 'single',
    mergedItems: [],
    trips: [],
    tripsById: {},
    tabTripIds: { upcoming: [], dispatched: [], 'in-transit': [], history: [], 'all-trips': [] },
    loadAssigmentMessageEditorOpen: false,
    isDispatchingAssignedLoad: false,
    assignTruckMenuOpen: false,
    tourAssigmentPreview: false,
    tourTruckAssigmentMessage: '',
    tripAssigmentMessageEditorOpen: false,
    multiAssignPlannedStops: [],
    // Filtering
    sort: '',
    searchQuery: '',
    dispatcherFilter: ['all'],
    driverFilter: ['all'],
    dispatcherAssignmentFilter: 'all',
    selectedLoadGroupId: null,
    brokerFilter: [],
    brokerFilterMode: 'priority', // Default to priority mode (show all, prioritize selected)
    laneFilterMode: 'all',
    tagFilter: [],
    payrollBatchFilter: [],
    includePayrollPlacement: false,
    stopTypeFilter: [StopTypeFilter.ALL],
    editLoadModalOpen: false,
    editLoadInitialStopIndex: null,
    expandedLoads: [],
    financeStatusFilter: 'all',
    attentionNeededFilter: false,
    unassignedRiskFilter: [],
    allTripsDateRange: {
        startDate: '',
        endDate: '',
    },
    loadsWithRecommendations: [],
    recommendationSummary: null,
    selectedDeadheadOrigin: null,
    selectedDeadheadOriginSource: null,
}

export const loadSlice: Slice<LoadsState> = createSlice({
    name: 'loads',
    initialState,
    extraReducers: (builder) =>
        builder.addCase(loadNotesReceived, (state, { payload }) => {
            applyLoadNotesToBuckets([state.upcomingLoads, state.dispatchedLoads, state.inTransitLoads, state.historyLoads], payload)
        }),
    reducers: {
        setSelectedLoadsIds(state, action: PayloadAction<string[]>) {
            state.selectedLoadsIds = action.payload
        },
        setActiveTab(state, action: PayloadAction<TabType>) {
            state.activeTab = action.payload
            state.shouldFetchLoads = true
        },
        setTableUiVersion(state, action: PayloadAction<OngoingLoadsTableUiVersion>) {
            state.tableUiVersion = action.payload
            persistTableUiVersion(action.payload)
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        setUpdating(state, action: PayloadAction<boolean>) {
            state.isUpdating = action.payload
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload
        },
        setCounts(state, action: PayloadAction<LoadsState['counts']>) {
            state.counts = action.payload
        },
        setTabFinancialSummary(state, action: PayloadAction<{ tab: TabType; summary: OngoingLoadsFinancialSummary }>) {
            const tabKey: keyof LoadsState['financialSummaries'] = tabToCountKey(action.payload.tab) ?? 'allTrips'
            state.financialSummaries[tabKey] = action.payload.summary
        },
        openBolCheckSidebar(state, action: PayloadAction<BolCheck>) {
            state.selectedBolCheck = action.payload
            state.isBolCheckSidebarOpen = true
        },
        closeBolCheckSidebar(state) {
            state.isBolCheckSidebarOpen = false
            state.selectedBolCheck = null
        },
        setSelectedLoad(state, action: PayloadAction<ClientOngoingLoad | null>) {
            state.selectedLoad = action.payload
        },
        setSearchQuery(state, action: PayloadAction<string>) {
            state.searchQuery = action.payload
            state.page = 1
        },
        setOngoingLoadCounts(state, action: PayloadAction<LoadsState['ongoingLoadCounts']>) {
            state.ongoingLoadCounts = action.payload
        },

        setPage(state, action: PayloadAction<number>) {
            if (state.page !== action.payload) {
                // Clear per-page state to prevent accumulation across pages
                state.expandedLoads = []
                state.selectedLoadsIds = []
                state.loadProcessingStatus = {}
            }
            state.page = action.payload
        },
        setRowsPerPage(state, action: PayloadAction<number>) {
            state.rowsPerPage = action.payload
            persistRowsPerPage(action.payload)
        },
        setFileUploadModalOpen(state, action: PayloadAction<boolean>) {
            state.fileUploadModalOpen = action.payload
        },
        setRateConsModalOpen(state, action: PayloadAction<boolean>) {
            state.rateConsModalOpen = action.payload
        },
        setShouldFetchLoads(state, action: PayloadAction<boolean>) {
            state.shouldFetchLoads = action.payload
        },
        setDeleteConfirmModalOpen(state, action: PayloadAction<boolean>) {
            state.deleteConfirmModalOpen = action.payload
        },
        setInTransitLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.inTransitLoads = action.payload
        },
        setDispatchedLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.dispatchedLoads = action.payload
        },
        setHistoryLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.historyLoads = action.payload
        },
        setAllTripsLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.allTripsLoads = action.payload
        },
        setUpcomingLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.upcomingLoads = action.payload
        },
        setLoadsWithRecommendations(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.loadsWithRecommendations = action.payload
        },
        setRecommendationSummary(state, action: PayloadAction<RecommendationSummary | null>) {
            state.recommendationSummary = action.payload
        },
        setTabToReset(state, action: PayloadAction<TabType | null>) {
            state.tabToReset = action.payload
        },
        updateSingleLoad: (state, action: PayloadAction<ClientOngoingLoad>) => {
            const updatedLoad = action.payload

            // Preserve invoice fields from the existing entry when the incoming update doesn't carry them.
            // Non-invoice endpoints (status change, ETA refresh, etc.) return bare loads without
            // accessorials/lineHaulRate/totalAmount, which would otherwise wipe out fields populated
            // by the invoice data merge in get-all-loads.
            const mergeWithExisting = (existing: ClientOngoingLoad): ClientOngoingLoad => {
                const merged = { ...updatedLoad }
                if (merged.accessorials == null && existing.accessorials != null) merged.accessorials = existing.accessorials
                if (merged.lineHaulRate == null && existing.lineHaulRate != null) merged.lineHaulRate = existing.lineHaulRate
                if (merged.fuelSurcharge == null && existing.fuelSurcharge != null) merged.fuelSurcharge = existing.fuelSurcharge
                if (merged.detentionCharge == null && existing.detentionCharge != null) merged.detentionCharge = existing.detentionCharge
                if (merged.lumperFee == null && existing.lumperFee != null) merged.lumperFee = existing.lumperFee
                if (merged.totalAmount == null && existing.totalAmount != null) merged.totalAmount = existing.totalAmount
                if (merged.paidAmount == null && existing.paidAmount != null) merged.paidAmount = existing.paidAmount
                return merged
            }

            const updateList = (list: ClientOngoingLoad[]) => list.map((load) => (load.id === updatedLoad.id ? mergeWithExisting(load) : load))

            if (state.activeTab === 'in-transit') {
                state.inTransitLoads = updateList(state.inTransitLoads)
            } else if (state.activeTab === 'dispatched') {
                state.dispatchedLoads = updateList(state.dispatchedLoads)
            } else if (state.activeTab === 'history') {
                state.historyLoads = updateList(state.historyLoads)
            } else if (state.activeTab === 'all-trips') {
                state.allTripsLoads = updateList(state.allTripsLoads)
            } else {
                state.upcomingLoads = updateList(state.upcomingLoads)
            }
        },
        setUpcomingPagination(state, action: PayloadAction<Pagination>) {
            state.upcomingPagination = action.payload
        },
        setInTransitPagination(state, action: PayloadAction<Pagination>) {
            state.inTransitPagination = action.payload
        },
        setDispatchedPagination(state, action: PayloadAction<Pagination>) {
            state.dispatchedPagination = action.payload
        },
        setHistoryPagination(state, action: PayloadAction<Pagination>) {
            state.historyPagination = action.payload
        },
        setAllTripsPagination(state, action: PayloadAction<Pagination>) {
            state.allTripsPagination = action.payload
        },
        setSelectedLoadId(state, action: PayloadAction<string | null>) {
            state.selectedLoadId = action.payload
        },

        setSelectedTruck(state, action: PayloadAction<Truck | undefined>) {
            state.selectedTruck = action.payload
        },
        setLoadProcessingStatus(state, action: PayloadAction<{ loadId: string; status: LoadProcessingStatus }>) {
            const { loadId, status } = action.payload
            state.loadProcessingStatus[loadId] = status
        },
        setMultiAssignResult(state, action: PayloadAction<AssignResult | null>) {
            state.multiAssignResult = action.payload
        },
        setIsLoadAssigning(state, action: PayloadAction<boolean>) {
            state.isLoadAssigning = action.payload
        },
        setMultiAssignStep(state, action: PayloadAction<WizardStep>) {
            state.multiAssignStep = action.payload
        },
        setCurrentTrip(state, action: PayloadAction<ClientOngoingTrip | null>) {
            state.currentTrip = action.payload
        },
        setSelectedLoads(state, action: PayloadAction<ClientOngoingLoad[]>) {
            state.selectedLoads = action.payload
        },

        setIsTripActionLoading(state, action: PayloadAction<'cancel' | 'archive' | 'delete' | null>) {
            state.isTripActionLoading = action.payload
        },
        setIsTripDeleteDialogOpen(state, action: PayloadAction<boolean>) {
            state.isTripDeleteDialogOpen = action.payload
        },
        setIsTripArchiveDialogOpen(state, action: PayloadAction<boolean>) {
            state.isTripArchiveDialogOpen = action.payload
        },
        setIsTripCancelDialogOpen(state, action: PayloadAction<boolean>) {
            state.isTripCancelDialogOpen = action.payload
        },
        setLoadAssignmentMode(state, action: PayloadAction<'single' | 'multi'>) {
            state.loadAssignmentMode = action.payload
        },
        setMergedItems(state, action: PayloadAction<MergedItem[]>) {
            state.mergedItems = action.payload
        },
        setTrips(state, action: PayloadAction<ClientOngoingTrip[]>) {
            state.trips = action.payload
        },
        addTrips(state, action: PayloadAction<ClientOngoingTrip[]>) {
            for (const t of action.payload) state.tripsById[String(t.id)] = t
        },

        // set the list of trip IDs for a tab (replace, not append)
        // Also prunes tripsById to only keep trips referenced by at least one tab
        setTabTripIds(state, action: PayloadAction<{ tab: TabType; tripIds: string[] }>) {
            state.tabTripIds[action.payload.tab] = action.payload.tripIds

            // Prune orphaned trips from tripsById
            const referencedIds = new Set<string>()
            for (const ids of Object.values(state.tabTripIds)) {
                for (const id of ids) referencedIds.add(id)
            }
            for (const id of Object.keys(state.tripsById)) {
                if (!referencedIds.has(id)) {
                    delete state.tripsById[id]
                }
            }
        },
        setLoadAssigmentMessageEditorOpen(state, action: PayloadAction<boolean>) {
            state.loadAssigmentMessageEditorOpen = action.payload
        },
        setIsDispatchingAssignedLoad(state, action: PayloadAction<boolean>) {
            state.isDispatchingAssignedLoad = action.payload
        },
        setAssignTruckMenuOpen(state, action: PayloadAction<boolean>) {
            state.assignTruckMenuOpen = action.payload
        },
        setTourAssigmentPreview(state, action: PayloadAction<boolean>) {
            state.tourAssigmentPreview = action.payload
        },
        setTourTruckAssigmentMessage(state, action: PayloadAction<string>) {
            state.tourTruckAssigmentMessage = action.payload
        },
        setTripAssigmentMessageEditorOpen(state, action: PayloadAction<boolean>) {
            state.tripAssigmentMessageEditorOpen = action.payload
        },
        setMultiAssignPlannedStops(state, action: PayloadAction<TripStop[]>) {
            state.multiAssignPlannedStops = action.payload
        },
        setDispatcherFilter(state, action: PayloadAction<string[]>) {
            state.dispatcherFilter = action.payload
            state.page = 1
        },
        setDriverFilter(state, action: PayloadAction<string[]>) {
            state.driverFilter = action.payload
            state.page = 1
        },
        setDispatcherAssignmentFilter(state, action: PayloadAction<'all' | 'assigned' | 'unassigned'>) {
            state.dispatcherAssignmentFilter = action.payload
            state.page = 1
        },
        setSelectedLoadGroupId(state, action: PayloadAction<string | null>) {
            state.selectedLoadGroupId = action.payload
        },
        setBrokerFilter(state, action: PayloadAction<string[]>) {
            state.brokerFilter = action.payload
        },
        setBrokerFilterMode(state, action: PayloadAction<'filter' | 'priority'>) {
            state.brokerFilterMode = action.payload
        },
        setLaneFilterMode(state, action: PayloadAction<'all' | 'only' | 'exclude'>) {
            state.laneFilterMode = action.payload
            state.page = 1
        },
        setStopTypeFilter(state, action: PayloadAction<StopTypeFilter[]>) {
            state.stopTypeFilter = action.payload
            state.page = 1
        },
        setTagFilter(state, action: PayloadAction<string[]>) {
            state.tagFilter = action.payload
            state.page = 1
        },
        setPayrollBatchFilter(state, action: PayloadAction<PayrollBatchFilterOption[]>) {
            state.payrollBatchFilter = action.payload
            state.page = 1
        },
        setIncludePayrollPlacement(state, action: PayloadAction<boolean>) {
            state.includePayrollPlacement = action.payload
        },
        setSort(state, action: PayloadAction<string>) {
            state.sort = action.payload
        },
        setEditLoadInitialStopIndex(state, action: PayloadAction<number | null>) {
            state.editLoadInitialStopIndex = action.payload
        },
        setEditLoadModalOpen(state, action: PayloadAction<boolean>) {
            state.editLoadModalOpen = action.payload
        },
        setExpandedLoads(state, action: PayloadAction<string[]>) {
            state.expandedLoads = action.payload
        },
        setFinanceStatusFilter(state, action: PayloadAction<LoadFinanceStatus | 'all'>) {
            state.financeStatusFilter = action.payload
            state.page = 1 // Reset to first page when filter changes
        },
        setAttentionNeededFilter(state, action: PayloadAction<boolean>) {
            state.attentionNeededFilter = action.payload
            state.page = 1 // Reset to first page when filter changes
        },
        setUnassignedRiskFilter(state, action: PayloadAction<number[]>) {
            state.unassignedRiskFilter = action.payload
            state.page = 1
        },
        setAllTripsDateRange(state, action: PayloadAction<LoadsState['allTripsDateRange']>) {
            state.allTripsDateRange = action.payload
            state.page = 1
        },
        setSelectedDeadheadOrigin(state, action: PayloadAction<{ origin: DeadheadOrigin | null; source: DeadheadOriginSource | null }>) {
            state.selectedDeadheadOrigin = action.payload.origin
            state.selectedDeadheadOriginSource = action.payload.source
        },
    },
})

export const {
    setSelectedLoadsIds,
    setActiveTab,
    setTableUiVersion,
    setLoading,
    setTabFinancialSummary,
    setSelectedLoad,
    setSearchQuery,
    setDeleteConfirmModalOpen,
    setOngoingLoadCounts,
    setPage,
    setRowsPerPage,
    setFileUploadModalOpen,
    setRateConsModalOpen,
    setShouldFetchLoads,
    setInTransitLoads,
    setDispatchedLoads,
    setHistoryLoads,
    setAllTripsLoads,
    setUpcomingLoads,
    setLoadsWithRecommendations,
    setRecommendationSummary,
    setTabToReset,
    updateSingleLoad,
    setUpcomingPagination,
    setInTransitPagination,
    setDispatchedPagination,
    setHistoryPagination,
    setAllTripsPagination,
    setSelectedLoadId,
    setSelectedTruck,
    setLoadProcessingStatus,
    setIsLoadAssigning,
    setMultiAssignStep,
    setCurrentTrip,
    setSelectedLoads,
    setLoadAssignmentMode,
    addTrips,
    setTabTripIds,
    setLoadAssigmentMessageEditorOpen,
    setIsDispatchingAssignedLoad,
    setAssignTruckMenuOpen,
    setTripAssigmentMessageEditorOpen,
    setMultiAssignPlannedStops,
    setDispatcherFilter,
    setDriverFilter,
    setSelectedLoadGroupId,
    setBrokerFilter,
    setBrokerFilterMode,
    setLaneFilterMode,
    setStopTypeFilter,
    setTagFilter,
    setPayrollBatchFilter,
    setIncludePayrollPlacement,
    setSort,
    setEditLoadInitialStopIndex,
    setEditLoadModalOpen,
    setExpandedLoads,
    setFinanceStatusFilter,
    setAttentionNeededFilter,
    setUnassignedRiskFilter,
    setAllTripsDateRange,
    setSelectedDeadheadOrigin,
} = loadSlice.actions

export const selectLoadState = (state: RootState) => state.loads
export const selectEditLoadModalOpen = (state: RootState) => state.loads.editLoadModalOpen
export const selectEditLoadInitialStopIndex = (state: RootState) => state.loads.editLoadInitialStopIndex
export const selectSelectedLoad = (state: RootState) => state.loads.selectedLoad
export const selectExpandedLoads = (state: RootState) => state.loads.expandedLoads
export const selectLoadProcessingStatus = (state: RootState) => state.loads.loadProcessingStatus
export const selectSelectedLoadsIds = (state: RootState) => state.loads.selectedLoadsIds
export const selectActiveTab = (state: RootState) => state.loads.activeTab
export const selectTableUiVersion = (state: RootState) => state.loads.tableUiVersion
export const selectFinanceStatusFilter = (state: RootState) => state.loads.financeStatusFilter

export default loadSlice.reducer
